import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../lib/axios';
import { 
  Award, BookOpen, AlertCircle, CheckCircle, Clock, Plus, TrendingUp, 
  FileText, Calendar, User, ExternalLink, GraduationCap, Briefcase,
  ArrowLeft, Target, Play
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

// CDA Credential Types from CDA Council
const CDA_CREDENTIAL_TYPES = [
  { id: 'infant_toddler', name: 'Infant-Toddler Center-Based', ageGroup: 'Birth to 36 months' },
  { id: 'preschool', name: 'Preschool Center-Based', ageGroup: '3-5 years' },
  { id: 'birth_to_five', name: 'Birth to Five Center-Based', ageGroup: 'Birth to 5 years' },
  { id: 'family_child_care', name: 'Family Child Care', ageGroup: 'Birth to 5 years' },
  { id: 'home_visitor', name: 'Home Visitor', ageGroup: 'Birth to 5 years' },
  { id: 'bilingual', name: 'Bilingual Specialization', ageGroup: 'Spanish/English' }
];

// Affordable CDA Course Providers (Under $500)
const CDA_COURSE_PROVIDERS = [
  { name: 'TickTock Training', url: 'https://www.ticktocktraining.org/', price: 'Affordable', highlight: true },
  { name: 'NICCM Fast Track', url: 'https://niccm.com/General/cda-fast-track.html', price: 'Under $500', highlight: true },
  { name: 'CDAclass.org', url: 'https://www.cdaclass.org', price: 'Monthly tuition' },
  { name: 'Care Courses', url: 'https://www.carecourses.com/publicpages/cdatraining', price: 'Budget-friendly' },
  { name: 'CCEI Online', url: 'https://www.cceionline.com/cda-certification-training-programs/', price: 'Affordable' },
  { name: 'ChildCareED', url: 'https://www.childcareed.com/cda.html', price: 'Free intro course' },
  { name: 'ProSolutions Training', url: 'https://www.prosolutionstraining.com/content/cda/', price: 'Self-paced' }
];

// Texas Pre-Service Training Topics (8-Hour)
const PRESERVICE_TOPICS = [
  { id: 'child_growth', name: 'Child Growth & Development', icon: '👶', hours: 2 },
  { id: 'supervision_safety', name: 'Supervision & Safety Practices', icon: '👁️', hours: 2 },
  { id: 'guidance', name: 'Guidance & Positive Interaction', icon: '💬', hours: 1.5 },
  { id: 'health_safety', name: 'Health & Communicable Disease Control', icon: '🏥', hours: 1.5 },
  { id: 'abuse_neglect', name: 'Abuse, Neglect & Mandatory Reporting', icon: '⚠️', hours: 1 },
  { id: 'safe_sleep', name: 'Safe Sleep & SIDS Prevention', icon: '😴', hours: 1 }
];

// Free Texas Training Resources
const FREE_TRAINING_RESOURCES = [
  { name: 'Texas A&M AgriLife - FREE Annual Training', url: 'https://childcare.tamu.edu/courses/texas-annual-training/', free: true },
  { name: 'Texas HHS Child Care Regulation', url: 'https://www.hhs.texas.gov/providers/protective-services-providers/child-care-regulation' }
];

const StaffProfile = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const [staffMember, setStaffMember] = useState(null);
  const [certifications, setCertifications] = useState([]);
  const [trainingRecords, setTrainingRecords] = useState([]);
  const [onboarding, setOnboarding] = useState(null);
  const [preserviceLogs, setPreserviceLogs] = useState([]);
  const [workHours, setWorkHours] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modal states
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);
  const [showCommitmentModal, setShowCommitmentModal] = useState(false);
  const [showCoachingModal, setShowCoachingModal] = useState(false);
  
  const [selectedCDA, setSelectedCDA] = useState('');
  const signatureRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [commitmentSigned, setCommitmentSigned] = useState(false);

  const [trainingForm, setTrainingForm] = useState({
    training_title: '',
    topic: '',
    hours: '',
    provider: '',
    training_date: new Date().toISOString().split('T')[0],
    is_8hr_preservice: true
  });

  useEffect(() => {
    fetchStaffProfile();
  }, [staffId]);

  const fetchStaffProfile = async () => {
    try {
      // Get staff info
      const allStaff = await axios.get('/staff');
      const staff = allStaff.data.find(s => s.id === staffId);
      setStaffMember(staff);

      // Get certifications & training
      const [certsRes, trainingRes] = await Promise.all([
        axios.get(`/staff-certifications?staff_id=${staffId}`).catch(() => ({ data: [] })),
        axios.get(`/training-records?staff_id=${staffId}`).catch(() => ({ data: [] }))
      ]);
      setCertifications(certsRes.data);
      setTrainingRecords(trainingRes.data);

      // Get onboarding & preservice logs
      try {
        const onboardingRes = await axios.get(`/staff-onboarding/${staffId}`);
        setOnboarding(onboardingRes.data);
      } catch (e) {
        setOnboarding(null);
      }

      try {
        const logsRes = await axios.get(`/preservice-training-logs/${staffId}`);
        setPreserviceLogs(logsRes.data);
      } catch (e) {
        setPreserviceLogs([]);
      }

      // Get work hours for CDA
      try {
        const hoursRes = await axios.get(`/staff-work-hours/${staffId}`);
        setWorkHours(hoursRes.data.total_hours || 0);
      } catch (e) {
        // Estimate based on days since creation
        const days = staff?.created_at ? Math.floor((Date.now() - new Date(staff.created_at).getTime()) / (1000 * 60 * 60 * 24)) : 0;
        setWorkHours(Math.min(days * 8, 500));
      }
    } catch (error) {
      console.error('Error fetching staff profile:', error);
      toast.error('Failed to load staff profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogTraining = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/preservice-training-logs', {
        staff_id: staffId,
        ...trainingForm,
        hours: parseFloat(trainingForm.hours)
      });
      toast.success('Training logged!');
      setShowTrainingModal(false);
      setTrainingForm({
        training_title: '',
        topic: '',
        hours: '',
        provider: '',
        training_date: new Date().toISOString().split('T')[0],
        is_8hr_preservice: true
      });
      fetchStaffProfile();
    } catch (error) {
      toast.error('Failed to log training');
    }
  };

  const createOnboarding = async () => {
    try {
      await axios.post('/staff-onboarding', {
        staff_id: staffId,
        hire_date: new Date().toISOString().split('T')[0],
        food_handler_required: false
      });
      toast.success('Onboarding record created');
      fetchStaffProfile();
    } catch (error) {
      toast.error('Failed to create onboarding');
    }
  };

  // Calculate progress
  const preservice8hr = preserviceLogs.filter(l => l.is_8hr_preservice).reduce((sum, l) => sum + (l.hours || 0), 0);
  const preservice16hr = preserviceLogs.filter(l => !l.is_8hr_preservice).reduce((sum, l) => sum + (l.hours || 0), 0);
  const totalPreservice = preservice8hr + preservice16hr;
  const workHoursProgress = Math.min((workHours / 480) * 100, 100);

  // Signature pad functions
  const startDrawing = (e) => {
    const canvas = signatureRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = signatureRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e293b';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = signatureRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!staffMember) {
    return (
      <div className="text-center py-12">
        <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Staff member not found</h3>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back
      </Button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-100 p-6">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center">
            <User className="w-10 h-10 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {staffMember.name}
            </h1>
            <p className="text-slate-600">{staffMember.email}</p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                📚 Pre-Service: {Math.min(totalPreservice, 24)}/24 hrs
              </span>
              <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                💼 Experience: {workHours}/480 hrs
              </span>
              {workHours >= 480 && totalPreservice >= 24 && (
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                  ✓ CDA Ready
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CDA 480 Experience Hours Progress */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold text-emerald-900">🎯 CDA Experience Hours (480 Required)</h2>
            <p className="text-sm text-emerald-700">Tracked from classroom attendance</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-emerald-900">{workHours}</p>
            <p className="text-sm text-emerald-600">of 480 hours</p>
          </div>
        </div>
        <Progress value={workHoursProgress} className="h-4 bg-emerald-100" />
        <div className="flex justify-between mt-2 text-xs text-emerald-600">
          <span>0</span><span>120</span><span>240</span><span>360</span><span>480 ✓</span>
        </div>
        {workHours >= 480 ? (
          <div className="mt-3 p-3 bg-green-100 border border-green-300 rounded-lg text-green-800 text-sm">
            ✅ <strong>480 hours complete!</strong> Ready for CDA application.
          </div>
        ) : (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            📊 <strong>{480 - workHours} more hours</strong> needed for CDA
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="cda">CDA Requirements</TabsTrigger>
          <TabsTrigger value="training">Texas Training</TabsTrigger>
          <TabsTrigger value="resources">Resources</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Pre-Service Training Progress */}
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">📖 Texas Pre-Service Training (24 Hours)</h2>
              <Button onClick={() => setShowTrainingModal(true)} size="sm">
                <Plus className="w-4 h-4 mr-1" /> Log Training
              </Button>
            </div>
            
            {/* 8-Hour Progress */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium">8-Hour Pre-Service (Before first day)</span>
                <span>{Math.min(preservice8hr, 8)}/8 hrs</span>
              </div>
              <Progress value={(Math.min(preservice8hr, 8) / 8) * 100} className="h-3" />
            </div>

            {/* 16-Hour Progress */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium">16-Hour Remaining (Within 90 days)</span>
                <span>{Math.min(preservice16hr, 16)}/16 hrs</span>
              </div>
              <Progress value={(Math.min(preservice16hr, 16) / 16) * 100} className="h-3" />
            </div>

            {/* Topics Checklist */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4">
              {PRESERVICE_TOPICS.map((topic) => {
                const hoursLogged = preserviceLogs.filter(l => l.topic === topic.id).reduce((sum, l) => sum + (l.hours || 0), 0);
                const complete = hoursLogged >= topic.hours;
                return (
                  <div key={topic.id} className={`p-2 rounded-lg text-sm ${complete ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`}>
                    <span>{topic.icon} {topic.name}</span>
                    <div className="text-xs text-slate-500">{hoursLogged}/{topic.hours} hrs</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Commitment & Coaching */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-100 p-5">
              <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                1-Year Commitment Letter
              </h3>
              <p className="text-sm text-slate-600 mb-3">
                Commit to working at this center for at least 1 year.
              </p>
              <Button onClick={() => setShowCommitmentModal(true)} variant="outline" className="w-full">
                {commitmentSigned ? '✓ Signed' : 'View/Sign Commitment'}
              </Button>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-100 p-5">
              <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                Director Coaching Session
              </h3>
              <p className="text-sm text-slate-600 mb-3">
                Review portfolio and documentation before observation.
              </p>
              <Button onClick={() => setShowCoachingModal(true)} variant="outline" className="w-full">
                Schedule Coaching
              </Button>
            </div>
          </div>

          {/* Create Onboarding if not exists */}
          {!onboarding && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-amber-800 mb-2">No onboarding record found.</p>
              <Button onClick={createOnboarding}>Create Onboarding Record</Button>
            </div>
          )}
        </TabsContent>

        {/* CDA Requirements Tab */}
        <TabsContent value="cda" className="mt-6 space-y-6">
          {/* CDA Credential Types */}
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-2">🎓 CDA Credential Types</h2>
            <p className="text-sm text-slate-600 mb-4">
              Select your credential type. Visit{' '}
              <a href="https://www.cdacouncil.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                cdacouncil.org
              </a>
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {CDA_CREDENTIAL_TYPES.map((cda) => (
                <div 
                  key={cda.id}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedCDA === cda.id ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-primary/30'
                  }`}
                  onClick={() => setSelectedCDA(cda.id)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-slate-900">{cda.name}</h3>
                    {selectedCDA === cda.id && <CheckCircle className="w-5 h-5 text-primary" />}
                  </div>
                  <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">{cda.ageGroup}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CDA Requirements Checklist */}
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">📋 CDA Requirements Checklist</h2>
            <div className="space-y-3">
              <div className={`flex items-center justify-between p-3 rounded-lg ${totalPreservice >= 24 ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  {totalPreservice >= 24 ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Clock className="w-5 h-5 text-slate-400" />}
                  <div>
                    <p className="font-medium">120 Hours Professional Education</p>
                    <p className="text-xs text-slate-500">Training in 8 CDA subject areas</p>
                  </div>
                </div>
                <span className="text-sm font-medium">{Math.min(totalPreservice * 5, 120)}/120</span>
              </div>
              
              <div className={`flex items-center justify-between p-3 rounded-lg ${workHours >= 480 ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  {workHours >= 480 ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Clock className="w-5 h-5 text-slate-400" />}
                  <div>
                    <p className="font-medium">480 Hours Work Experience</p>
                    <p className="text-xs text-slate-500">Working with children in your setting</p>
                  </div>
                </div>
                <span className="text-sm font-medium">{workHours}/480</span>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="font-medium">Professional Portfolio</p>
                    <p className="text-xs text-slate-500">Documentation of competencies</p>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded">Pending</span>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="font-medium">Verification Visit</p>
                    <p className="text-xs text-slate-500">By CDA PD Specialist — Rachel Garibay (#P000044296), San Antonio</p>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 bg-slate-200 text-slate-600 rounded">Not Scheduled</span>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="font-medium">CDA Exam</p>
                    <p className="text-xs text-slate-500">65-question assessment</p>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 bg-slate-200 text-slate-600 rounded">Not Scheduled</span>
              </div>
            </div>
          </div>

          {/* CDA Profile Guide */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                <Play className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-blue-900 mb-2">📹 How to Create Your CDA Profile</h3>
                <p className="text-sm text-blue-700 mb-3">
                  Set up your CDA Council profile, enter center director info for experience validation, and apply.
                </p>
                <a href="https://www.cdacouncil.org/en/credentials/how-to-apply/" target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium">
                  CDA Council Application Guide <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Texas Training Tab */}
        <TabsContent value="training" className="mt-6 space-y-6">
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">📖 Texas 24-Hour Annual Training Requirements</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">Required 6+ Hours</span>
                  Core Topics
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-slate-50 rounded">Child Growth & Development</div>
                  <div className="p-2 bg-slate-50 rounded">Guidance & Discipline</div>
                  <div className="p-2 bg-slate-50 rounded">Age-Appropriate Curriculum</div>
                  <div className="p-2 bg-slate-50 rounded">Teacher-Child Interaction</div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">Required 1 Hour</span>
                  Child Abuse Prevention
                </h3>
                <div className="p-2 bg-slate-50 rounded text-sm">Child Abuse & Neglect Prevention/Reporting</div>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">Infant/Toddler</span>
                  Additional Requirements
                </h3>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="p-2 bg-slate-50 rounded">Shaken Baby Syndrome/Abusive Head Trauma</div>
                  <div className="p-2 bg-slate-50 rounded">Safe Sleep Practices & SIDS Prevention</div>
                  <div className="p-2 bg-slate-50 rounded">Early Childhood Brain Development</div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">Additional</span>
                  Safety & Health
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-slate-50 rounded">Emergency Preparedness</div>
                  <div className="p-2 bg-slate-50 rounded">Communicable Disease Prevention</div>
                  <div className="p-2 bg-slate-50 rounded">Medication Administration</div>
                  <div className="p-2 bg-slate-50 rounded">Food & Allergic Reaction Response</div>
                  <div className="p-2 bg-slate-50 rounded">Building & Physical Premises Safety</div>
                  <div className="p-2 bg-slate-50 rounded">Hazardous Materials Handling</div>
                </div>
              </div>
            </div>
          </div>

          {/* Free Training */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
            <h2 className="text-lg font-bold text-emerald-900 mb-4">🆓 FREE Texas Training Resources</h2>
            <div className="space-y-3">
              {FREE_TRAINING_RESOURCES.map((r) => (
                <a key={r.name} href={r.url} target="_blank" rel="noopener noreferrer"
                   className="flex items-center justify-between p-4 bg-white rounded-lg border border-emerald-200 hover:border-emerald-400 transition-colors">
                  <div>
                    <p className="font-medium text-emerald-900">{r.name}</p>
                    {r.free && <span className="text-xs text-emerald-600 font-medium">FREE MODULES</span>}
                  </div>
                  <ExternalLink className="w-5 h-5 text-emerald-600" />
                </a>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources" className="mt-6 space-y-6">
          {/* Affordable CDA Courses */}
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-2">💰 Affordable CDA Courses (Under $500)</h2>
            <p className="text-sm text-slate-600 mb-4">
              These provide 120 training hours. Council application fee ($425-$525) is separate.
            </p>
            
            <div className="space-y-3">
              {CDA_COURSE_PROVIDERS.map((p) => (
                <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer"
                   className={`flex items-center justify-between p-4 rounded-lg border-2 hover:border-primary/50 transition-colors ${
                     p.highlight ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'
                   }`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900">{p.name}</p>
                      {p.highlight && <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs rounded">Recommended</span>}
                    </div>
                    <span className="text-xs text-emerald-600 font-medium">{p.price}</span>
                  </div>
                  <ExternalLink className="w-5 h-5 text-slate-400" />
                </a>
              ))}
            </div>
          </div>

          {/* San Antonio Observers */}
          <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="san-antonio-observers-section">
            <h2 className="text-lg font-bold text-slate-900 mb-2">👁️ San Antonio CDA Observers</h2>
            <p className="text-sm text-slate-600 mb-4">
              Your CDA Verification Visit must be completed by a CDA Professional Development (PD) Specialist.
              Contact a specialist below to schedule your observation in San Antonio.
            </p>
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200" data-testid="observer-rachel-garibay">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
                    <User className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Rachel Garibay</p>
                    <p className="text-sm text-slate-600">CDA Professional Development Specialist</p>
                    <p className="text-xs text-slate-500">San Antonio, TX</p>
                  </div>
                </div>
                <span className="px-2.5 py-1 bg-white border border-emerald-300 text-emerald-800 text-xs font-medium rounded-full whitespace-nowrap">
                  PD Specialist #P000044296
                </span>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Don't see your specialist? Search the official directory at{' '}
              <a href="https://www.cdacouncil.org/en/pd-specialists/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                cdacouncil.org/pd-specialists
              </a>
            </p>
          </div>

          {/* Official Links */}
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">🔗 Official Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <a href="https://www.cdacouncil.org" target="_blank" rel="noopener noreferrer"
                 className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-primary/30">
                <Award className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="font-medium text-slate-900">CDA Council</p>
                  <p className="text-sm text-slate-600">Official credential info</p>
                </div>
              </a>
              <a href="https://www.hhs.texas.gov/providers/protective-services-providers/child-care-regulation" target="_blank" rel="noopener noreferrer"
                 className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-primary/30">
                <FileText className="w-8 h-8 text-emerald-600" />
                <div>
                  <p className="font-medium text-slate-900">Texas HHS</p>
                  <p className="text-sm text-slate-600">Licensing regulations</p>
                </div>
              </a>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Log Training Modal */}
      <Dialog open={showTrainingModal} onOpenChange={setShowTrainingModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Log Pre-Service Training</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLogTraining} className="space-y-4 mt-4">
            <div>
              <Label>Training Title</Label>
              <Input value={trainingForm.training_title} onChange={(e) => setTrainingForm({...trainingForm, training_title: e.target.value})} required className="mt-1.5" />
            </div>
            <div>
              <Label>Topic</Label>
              <Select value={trainingForm.topic} onValueChange={(v) => setTrainingForm({...trainingForm, topic: v})}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select topic" /></SelectTrigger>
                <SelectContent>
                  {PRESERVICE_TOPICS.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.icon} {t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hours</Label>
                <Input type="number" step="0.5" value={trainingForm.hours} onChange={(e) => setTrainingForm({...trainingForm, hours: e.target.value})} required className="mt-1.5" />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={trainingForm.training_date} onChange={(e) => setTrainingForm({...trainingForm, training_date: e.target.value})} required className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Provider</Label>
              <Input value={trainingForm.provider} onChange={(e) => setTrainingForm({...trainingForm, provider: e.target.value})} placeholder="e.g., Texas A&M AgriLife" className="mt-1.5" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={trainingForm.is_8hr_preservice} onCheckedChange={(c) => setTrainingForm({...trainingForm, is_8hr_preservice: c})} />
              <Label>Counts toward 8-Hour Pre-Service</Label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1">Log Training</Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowTrainingModal(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Commitment Letter Modal */}
      <Dialog open={showCommitmentModal} onOpenChange={setShowCommitmentModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>1-Year Commitment Letter</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-sm">
              <p className="font-semibold mb-2">Commitment Agreement</p>
              <p className="mb-2">
                I, <strong>{staffMember.name}</strong>, commit to working at this child care center for a minimum 
                of one (1) year from my start date, to support my professional development and CDA credential journey.
              </p>
              <p>
                I understand the center is investing in my training and I agree to fulfill this commitment.
              </p>
            </div>
            <div>
              <Label>Your Signature</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white mt-1.5">
                <canvas ref={signatureRef} width={400} height={100} className="w-full cursor-crosshair touch-none"
                  onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} />
              </div>
              <div className="flex justify-between mt-1">
                <p className="text-xs text-slate-500">Sign above</p>
                <Button type="button" variant="ghost" size="sm" onClick={clearSignature}>Clear</Button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button className="flex-1" onClick={() => { setCommitmentSigned(true); setShowCommitmentModal(false); toast.success('Commitment signed!'); }}>
                Sign Commitment
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setShowCommitmentModal(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Coaching Session Modal */}
      <Dialog open={showCoachingModal} onOpenChange={setShowCoachingModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Director Coaching Session</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <p className="text-sm text-slate-600">
              Schedule a session to review your CDA portfolio and documentation before observation.
            </p>
            <div>
              <Label>Preferred Date</Label>
              <Input type="date" className="mt-1.5" />
            </div>
            <div>
              <Label>Session Length</Label>
              <Select>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select duration" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="60">60 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea placeholder="What would you like to discuss?" className="mt-1.5" />
            </div>
            <div className="flex gap-2">
              <Button className="flex-1" onClick={() => { setShowCoachingModal(false); toast.success('Coaching session requested!'); }}>
                Request Appointment
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setShowCoachingModal(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffProfile;
